import confetti from 'canvas-confetti';
import { motion } from 'motion/react';
import { ReactNode, useEffect } from 'react';

import { brandColors } from '@utils/color-utils';

interface BelowHeaderNotificationProps {
  isOpen: boolean;
  onClose: () => void;
  message: ReactNode;
  type: 'success' | 'info' | 'error' | 'confetti';
  tradeType?: 'CALL' | 'PUT';
  showConfetti?: boolean;
  leftContent?: ReactNode;
}

export function BelowHeaderNotification({
  isOpen,
  onClose,
  message,
  type,
  tradeType = 'CALL',
  showConfetti = false,
  leftContent,
}: BelowHeaderNotificationProps) {
  // Trigger confetti effect for confetti type
  useEffect(() => {
    if (!isOpen || !showConfetti) {
      return;
    }

    const isCall = tradeType === 'CALL';
    confetti({
      particleCount: 150,
      startVelocity: 30,
      spread: 360,
      ticks: 120,
      zIndex: 10000,
      scalar: 1.2,
      shapes: ['star', 'circle'],
      colors: isCall
        ? [brandColors.green, '#ffffff', '#ffff00', '#00ff80']
        : [brandColors.red, '#ffffff', '#ff7777', '#ff9999'],
      origin: { x: 0.5, y: 0.2 },
      gravity: 0.8,
      drift: 0.1,
    });
  }, [isOpen, showConfetti, tradeType]);

  if (!isOpen) {
    return null;
  }

  // Determine accent color based on type and trade type
  const getAccentColor = () => {
    if (type === 'error') {
      return brandColors.red;
    }
    if (type === 'confetti') {
      return tradeType === 'CALL' ? brandColors.green : brandColors.red;
    }
    return brandColors.green; // success/info default
  };

  const accentColor = getAccentColor();

  return (
    <motion.div
      layout
      className="w-full max-w-md pointer-events-none"
      initial={{ opacity: 0, y: -20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.9 }}
      transition={{
        layout: { type: 'spring', damping: 30, stiffness: 400 },
        opacity: { duration: 0.2 },
        y: { type: 'spring', damping: 30, stiffness: 400 },
        scale: { duration: 0.2 },
      }}
    >
      <motion.div
        className="pointer-events-auto backdrop-blur-[3px] bg-white/5 rounded-2xl shadow-2xl border border-white/10 overflow-hidden"
        style={{
          boxShadow: `0 0 20px ${accentColor}20, 0 8px 32px rgba(0,0,0,0.4)`,
        }}
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
      >
        <div className="flex items-center gap-4 p-4">
          {/* Left content (icon, image, etc.) */}
          {leftContent && <div className="flex-shrink-0">{leftContent}</div>}

          {/* Message content */}
          <div className="flex-1 min-w-0">
            {typeof message === 'string' ? (
              <div
                className="text-base font-medium"
                style={{ color: accentColor }}
              >
                {message}
              </div>
            ) : (
              <div style={{ color: accentColor }}>{message}</div>
            )}
          </div>

          {/* Close button */}
          <button
            data-testid="close-notification"
            onClick={onClose}
            className="flex-shrink-0 text-white/40 hover:text-white/80 transition-colors"
            aria-label="Close notification"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
